import { FC, useCallback, useEffect, useState } from "react";
import GridWrapper from "../../../../components/controls/GridWrapper";
import { DateInput, TextInput } from "../../../../components/controls/inputs";
import useGridFilter, { GridFilterAction } from "../../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../../system/providers/appContextProvider";
import ContractorSelectorInput from "../../../../components/controls/inputs/ContractorSelectorInput";
import сreateInvoiceCashOrderStyles from "../styles/CreateInvoiceCashOrders.module.scss";
import { v4 as uuidv4, NIL as uuidNull } from "uuid";
import { ICostInfoDTO, IDocumentInfoDTO, IEntitySimpleDTO } from "../../../../libs/coreapi-dto/@types/common";
import BaseDocumentCreator from "../../../../components/documents/baseDocumentCreator";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { useForm } from "../../../../system/hooks/useForm";
import { IDocumentUpdateFormProps } from "../../../../@types/documents";
import { IKeyValuePair } from "../../../Reports/Shared/Interfaces/IKeyValuePair";
import { DocumentTypeRu, IdTableVariant } from "../../../../@types/enumsGlobal";
import { BtSaveOrOk } from "../../../Documents/Invoice/InvoiceCreator/InvoiceUpdateForm";
import { MessageModalWindow } from "../../../../components/modalWindows/MessageModalWindow";
import { DecimalInputV2 } from "../../../../components/controls/inputs/BaseInput";
import FlexRow from "../../../../components/controls/FlexRow";
import useLockingDocuments from "../../../../components/lockDocuments/useLockingDocuments";
import { useUserContext } from "../../../../system/providers/userContextProvider";
import { checkLockStatus } from "../../../CommonHelperFunctions";
import { IInvoiceCashOrderCreateDTO, IInvoiceCashOrderEditDTO, IInvoiceCashOrderGetDTO } from "../../../../libs/coreapi-dto/documents/invoiceCashOrder";
import TaxTypeSelectorInput from "../../../../components/controls/inputs/TaxTypeSelectorInput";
import { SelectDocumentModalWindow } from "../../../../components/modalWindows/SelectDocumentModalWindow";
import { DictionaryInput } from "../../../../components/controls/dictionaryInput";
import { User2ContractorDataProvider } from "../../../../Services/DataProviders/User2ContractorDataProvider";
import { IUser2ContractorViewDTO } from "../../../../libs/coreapi-dto/dirs/user2Contractor";
import { ContractorDataProvider } from "../../../../Services/DataProviders/ContractorDataProvider";
import { CheckBox } from "../../../../components/controls/checkbox";
import { TaxTypeDataProvider } from "../../../../Services/DataProviders/TaxTypeDataProvider";
import { ITaxTypeDTO } from "../../../../libs/coreapi-dto/dirs/taxType";
import { InvoiceOutDataProvider } from "../../../../Services/DataProviders/InvoiceOutDataProvider";
import { MovementDataProvider } from "../../../../Services/DataProviders/MovementDataProvider";
import { CashSessionDataProvider } from "../../../../Services/DataProviders/CashSessionDataProvider";
import useInvoiceCashOrderState from "./invoiceCashOrderState";
import FlexColumn from "../../../../components/controls/FlexColumn";

interface IValidator {
    documentDate: DateTime;
    contractorFrom: IEntitySimpleDTO;
    sumPay: number;
}

interface IMessageModalProps {
    show: boolean;
    message: string;
    btSaveOrOk?: BtSaveOrOk;
}

interface IReturnCreatedItem {
    displayItem: any;
    createdItem: any;
}

interface IBaseDocumentChanged {
    isBaseDocumentInfoChanged: boolean,
    baseDocumentInfo: { idTable: number; idGlobal: string; displayName: string },
    documentCostInfo: IDocumentItemCostInfoDTO
}

export const InvoiceCashOrderUpdateForm: FC<IDocumentUpdateFormProps<IInvoiceCashOrderGetDTO, null, IInvoiceCashOrderEditDTO>> = (props) => {
    const appContext = useAppContext();

    const lockingDocuments = useLockingDocuments();
    const [idLockingDocument, setIdLockingDocument] = useState<string | undefined>();
    const userContext = useUserContext();
    const lockFromPermission = checkLockStatus(props.permission as IPermission, userContext.userPermission);
    const documentStateProc = (props.variant === 'edit' && (props.document?.documentState === 'proc' || lockFromPermission)) ? true : false;

    const user2ContractorDataProvider = new User2ContractorDataProvider(appContext.coreApiService);
    const contractorDataProvider = new ContractorDataProvider(appContext.coreApiService);
    const taxTypeDP = new TaxTypeDataProvider(appContext.coreApiService);
    const movementDP = new MovementDataProvider(appContext.coreApiService);
    const cashSessionDP = new CashSessionDataProvider(appContext.coreApiService);
    const invoiceOutDP = new InvoiceOutDataProvider(appContext.coreApiService);

    const [entity, setEntity] = useState<IInvoiceCashOrderEditDTO>({} as IInvoiceCashOrderEditDTO);
    const invoiceCashOrder = useInvoiceCashOrderState(props.idGlobal ?? uuidv4(), props.document, (value) => setEntity(value));
    const [mnemocode, setMnemocode] = useState<string>(props.document?.mnemocode ?? "");
    const [documentState, setDocumentState] = useState<string>(props.document?.documentState ?? "Новый");

    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({
        show: false,
        message: "",
    });

    const [gridFilterContractors4User, dispatchGridFilterContractors4User] = useGridFilter();
    const [gridFilterUser2Contractor] = useGridFilter({
        columnFilters: [
            { name: "IdUserGlobal", value: userContext.idGlobal, operator: 'Eq' } as IGridColumnFilter,
        ]
        } as IGridFilter);

    const { t } = useTranslation();
    const baseT = (value: string) => t("documents.invoiceCashOrder." + value);
    const errorsT = (value: string) => t("errors." + value);

    
    const [disabled, setDisabled] = useState<boolean>(false);
    const [isIncludeVAT, setIsIncludeVAT] = useState<boolean>(props.documentUpdate?.includeVat ? props.documentUpdate?.includeVat : false);

    const [contractors2User, setContractors2User] = useState<IUser2ContractorViewDTO[]>([]);
    const [selectBaseDocument, setSelectBaseDocument] = useState<boolean>(false);

    const [showMessageModalValidDocument, setShowMessageModalValidDocument] = useState<IMessageModalProps>({ show: false, message: "" });

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            documentDate: {
                required: {
                    value: invoiceCashOrder?.documentDate?.value?.day ? false : true,
                    message: errorsT("required")
                }
            },
            contractorFrom: {
                required: {
                    value: !invoiceCashOrder?.idContractorFromGlobal?.value || invoiceCashOrder?.idContractorFromGlobal?.value === uuidNull,
                    message: errorsT("required")
                }
            },
            sumPay: {
                required: {
                    value: invoiceCashOrder?.retailSumIncVat?.value ? false : true,
                    message: errorsT("required")
                }
            },

        },
    });

    useEffect(() => {
        invoiceCashOrder.entitySet.setValue(props.document);
    }, []);

    useEffect(() => {
        const handleTabClose = (event) => {
            // operativeReserve.deleteAll({
            //     idTable: IdTableVariant.InternetOrder,
            //     idDocument: data.idGlobal as string,
            // });
            lockingDocuments.delete({
                idTable: IdTableVariant.InternetOrder,
                idDocument: invoiceCashOrder?.idGlobal as string,
            });
            return (event.returnValue = "");
        };

        window.addEventListener("beforeunload", handleTabClose);

        return () => {
            window.removeEventListener("beforeunload", handleTabClose);
        };
    }, []);

    useEffect(() => {
        contractorDataProvider.getMe((value) => 
        {
            let contractorMe = { idGlobal : value.idGlobal, displayName : value.name } as IEntitySimpleDTO;
            invoiceCashOrder.contractorOwner.setValue(contractorMe);
            if (!(props.document?.contractorOwner?.idGlobal && props.document?.contractorOwner?.idGlobal !== uuidNull)) {
                invoiceCashOrder.idContractorOwnerGlobal.setValue(contractorMe.idGlobal);
            }
        }
        );
    }, []);

    useEffect(() => {
            user2ContractorDataProvider.getView(gridFilterUser2Contractor, (values) => {
                setContractors2User(values)
               })
    }, []);

    useEffect(() => {
        if (props.document?.taxType?.idGlobal && props.document?.taxType?.idGlobal !== uuidNull) {
            taxTypeDP.getById(props.document?.taxType.idGlobal, (taxType: ITaxTypeDTO) => {
                invoiceCashOrder.taxRate.setValue(taxType.taxRate);
            })
        }  
    }, []
    )

    useEffect(() => {
        if (props.variant === "edit" && props.documentUpdate && props.document && invoiceCashOrder.idGlobal) {

            lockingDocuments.check(invoiceCashOrder.idGlobal as string, (e) => {
                if (e)
                    props.cancel();
                else
                    lockingDocuments.send({
                        idTable: IdTableVariant.InvoiceCashOrder,
                        idDocument: invoiceCashOrder.idGlobal as string,
                    }, (e) => {
                        setIdLockingDocument(e)
                    });
            })

            setMnemocode(props.document.mnemocode);
            setDocumentState(props.document.documentState);
        }
    }, [props.variant]);

    useEffect(() => {
        if (invoiceCashOrder.idTaxTypeGlobal?.value?.length > 0 && invoiceCashOrder?.idTaxTypeGlobal?.value !== uuidNull) {
            taxTypeDP.getById(invoiceCashOrder?.idTaxTypeGlobal?.value, (taxType: ITaxTypeDTO) => {
                invoiceCashOrder.taxRate.setValue(taxType.taxRate);
                let vatSumNew = isIncludeVAT ? getVATSumIncludeVAT(invoiceCashOrder?.retailSumIncVat?.value, taxType.taxRate) : getVATSumNotIncludeVAT(invoiceCashOrder?.retailSumIncVat?.value, taxType.taxRate);
                invoiceCashOrder.taxRateSet.setValue(invoiceCashOrder?.idTaxTypeGlobal?.value, vatSumNew);
            })
        }
    }, [invoiceCashOrder.idTaxTypeGlobal.value]
    )

    const onContractors4UserChanged = (user2ContractorArray?: IUser2ContractorViewDTO[]): GridFilterAction => {
        const inExpression =  user2ContractorArray?.map(x => { return `'` + x.idContractorGlobal + `'`}).join(',')
        const expression = `"IdGlobal" in (${inExpression})`

        return {
            type: "addCustomWhereExpressionFilter",
            payload: expression,
        } as GridFilterAction;
    };

    useEffect(() => 
    {
        if (contractors2User?.length > 0)
        {
            dispatchGridFilterContractors4User(onContractors4UserChanged(contractors2User));
        }
    }
    , [contractors2User]);
 
    useEffect(() => 
    { 
        if (invoiceCashOrder.idContractorFromGlobal.value &&
            invoiceCashOrder.idContractorFromGlobal.value !== uuidNull) {
                updateIncludeVat(invoiceCashOrder.idContractorFromGlobal.value);
            }
    }
    , [invoiceCashOrder.idContractorFromGlobal.value]);

    const isValidDocument = (entity: IInvoiceCashOrderCreateDTO, showMessage: () => void): boolean => {
        var flag = true;
        if (!entity || 
            !entity.idContractorFromGlobal || 
            !invoiceCashOrder?.documentDate?.value ||
            !invoiceCashOrder?.idContractorFromGlobal?.value ||
            invoiceCashOrder?.idContractorFromGlobal?.value === uuidNull ||
            !invoiceCashOrder?.retailSumIncVat?.value
        ) {
            flag = false;
            showMessage();
        }
        return flag;
    }

    const getData2Save = ():IInvoiceCashOrderCreateDTO =>
    {
            let dataSave = {
                idGlobal : invoiceCashOrder.idGlobal,
                documentDate : invoiceCashOrder.documentDate.value,
                baseDocumentInfo : invoiceCashOrder.baseDocumentInfo.value,
                idContractorFromGlobal : invoiceCashOrder.idContractorFromGlobal.value,
                idContractorOwnerGlobal : invoiceCashOrder.idContractorOwnerGlobal.value,
                idContractsGlobal : invoiceCashOrder.idContractsGlobal.value,
                idTaxTypeGlobal : invoiceCashOrder.idTaxTypeGlobal.value,
                idUserGlobal : userContext.idGlobal,
                includeVat : invoiceCashOrder.isIncludeVATActive.value ? isIncludeVAT : false, //data.includeVat,
                supplement : invoiceCashOrder.supplement.value,
                baseText : invoiceCashOrder.baseText.value,
                supplierSum: invoiceCashOrder.supplierSum.value,
                supplierVatSum: invoiceCashOrder.supplierVatSum.value,
                supplierSumIncVat: invoiceCashOrder.supplierSumIncVat.value,
                retailSum: invoiceCashOrder.retailSum.value,
                retailVatSum: invoiceCashOrder.retailVatSum.value,
                retailSumIncVat: invoiceCashOrder.retailSumIncVat.value

            } as IInvoiceCashOrderCreateDTO; 

            return dataSave;
    };

    const onOkAction = (dataSave: IInvoiceCashOrderCreateDTO) => {
        lockingDocuments.delete({
            idTable: IdTableVariant.InvoiceCashOrder,
            idDocument: invoiceCashOrder?.idGlobal as string,
        });

        
        props.ok(dataSave);
    };

    const onOk = () => {
        if (isValid()) {
            let dataSave = getData2Save();
            onOkAction(dataSave);
        }
    };

    const onSave = () => {
        if (isValid()) {
            setErrors({ ...errors, documentDate: undefined, contractorFrom: undefined, sumPay: undefined })
            let dataSave = getData2Save();
            props.save(dataSave);
        }
    };

    const onCancel = () => {
        // operativeReserve.deleteAll(
        //     {
        //         idTable: IdTableVariant.InvoiceCashOrder,
        //         idDocument: invoiceCashOrder.idGlobal as string,
        //     },
        //     () => {
        //         props.cancel();
        //     }
        // );
        lockingDocuments.delete({
            idTable: IdTableVariant.InvoiceCashOrder,
            idDocument: invoiceCashOrder.idGlobal as string,
        });
        props.cancel();
    };

    const getVATSumNotIncludeVAT = (paySum: number, taxRate: number): number => {
        let vatSum = (paySum / 100) * taxRate;
        return vatSum;
    }

    const getVATSumIncludeVAT = (paySum: number, taxRate: number): number => {
        let vatSum = paySum - (paySum / (1 + taxRate / 100));
        return vatSum;
    }

    const getVatSumOrigin = () => {
        let vatSumOrigin = props.document?.retailCostInfo.vatSum;
        return vatSumOrigin;
    }

    const onChangeInput = useCallback(
    (e: IBaseDocumentChanged, document: IInvoiceCashOrderEditDTO) => {
        if (e && document) {
            let idContractorGlobal = updateSumParameters(e, document);
        }
    }, []);

    const updateSumParameters = (e: IBaseDocumentChanged, document: IInvoiceCashOrderEditDTO) => {
        if (e && 
            e.baseDocumentInfo &&
            e.baseDocumentInfo.idTable &&
            e.baseDocumentInfo.idGlobal && 
            e.baseDocumentInfo.idGlobal !== uuidNull) {
                let sumIncVat = null as number | null;
                let vatSum = null as number | null;

                let idTable = e.baseDocumentInfo.idTable;
                let idDocument = e.baseDocumentInfo.idGlobal;

                let supplierCostInfo = {} as ICostInfoDTO;
                let retailCostInfo = {} as ICostInfoDTO;
                let contractorTo = {} as IEntitySimpleDTO;

                const documentEdit: IInvoiceCashOrderEditDTO = {
                        idGlobal: document.idGlobal as string,
                        documentDate: document.documentDate.toF3DateTimeServerSide(userContext),
                        baseDocumentInfo : document.baseDocumentInfo,
                        supplement : document.supplement,
                        idTaxTypeGlobal : document.idTaxTypeGlobal,
                        baseText : document.baseText,
                        idUserGlobal : document.idUserGlobal,
                        idContractsGlobal : document.idContractsGlobal,
                        includeVat : document.includeVat,
                        idContractorOwnerGlobal : document.idContractorOwnerGlobal,
                        idContractorFromGlobal : document.idContractorFromGlobal,
                        supplierSum : document.supplierSum,
                        supplierVatSum : document.supplierVatSum,
                        supplierSumIncVat : document.supplierSumIncVat,
                        retailSum : document.retailSum,
                        retailVatSum : document.retailVatSum,
                        retailSumIncVat : document.retailSumIncVat
                    };

                if (idTable == 8) {
                    let columnsFilters = [] as IViewFilter[];
                    let filter = {name: "IdGlobal", value: idDocument, operator: 'Eq'} as IViewFilter;
                    columnsFilters?.push(filter);
                    let viewBody = {  pageNumber: 1, numberPerPage: 1, columnFilters: columnsFilters} as IViewBody ;
                    movementDP.getView(viewBody, (movements, totalCount) => {
                        if (movements && movements?.length > 0) {
                        let movement = movements[0];
                        sumIncVat = movement.retailSumIncVat;
                        vatSum = movement.retailVatSum;
                        retailCostInfo = { sumIncVat: sumIncVat, vatSum: vatSum } as ICostInfoDTO;
                        contractorTo = { idGlobal: movement.idContractorToGlobal, displayName: movement.contractorTo } as IEntitySimpleDTO;

                        if (e.isBaseDocumentInfoChanged) {
                           let baseDocumentInfo = { idTable: idTable, idGlobal: idDocument, displayName: e.baseDocumentInfo.displayName };
                           let documentCostInfo = { supplierCostInfo: supplierCostInfo, retailCostInfo: retailCostInfo} as IDocumentItemCostInfoDTO;
                           
                          
                           invoiceCashOrder.baseDocumentSet.setValue(documentEdit, baseDocumentInfo, documentCostInfo, contractorTo);

                        }

                        }
                    });
                }
                else 
                    if (idTable == 19) {
                    cashSessionDP.getSumParameters(idDocument, (cashSession) => {
                        sumIncVat = cashSession?.data?.chequeSum;
                        vatSum = cashSession?.data?.retailVatSum;
                        retailCostInfo = { sumIncVat: sumIncVat, vatSum: vatSum } as ICostInfoDTO;
                        contractorTo = { idGlobal: cashSession?.data?.idContractorGlobal, displayName: cashSession?.data?.contractorName } as IEntitySimpleDTO;

                        if (e.isBaseDocumentInfoChanged) {
                            let baseDocumentInfo = { idTable: idTable, idGlobal: idDocument, displayName: e.baseDocumentInfo.displayName };
                            let documentCostInfo = { supplierCostInfo: supplierCostInfo, retailCostInfo: retailCostInfo} as IDocumentItemCostInfoDTO;
                            invoiceCashOrder.baseDocumentSet.setValue(documentEdit, baseDocumentInfo, documentCostInfo, contractorTo);
                        }
                    });
                }
                else if (idTable == 222) {
                    let columnsFilters = [] as IViewFilter[];
                    let filter = {name: "IdGlobal", value: idDocument, operator: 'Eq'} as IViewFilter;
                    columnsFilters?.push(filter);
                    let viewBody = {  pageNumber: 1, numberPerPage: 1, columnFilters: columnsFilters} as IViewBody ;
                    invoiceOutDP.getView(viewBody, (invoiceOuts, totalCount) => {
                        if (invoiceOuts && invoiceOuts?.length > 0) {
                        let invoiceOut = invoiceOuts[0];
                        sumIncVat = invoiceOut.retailSumIncVat;
                        vatSum = invoiceOut.retailVatSum;
                        retailCostInfo = { sumIncVat: sumIncVat, vatSum: vatSum } as ICostInfoDTO;
                        contractorTo = { idGlobal: invoiceOut.idContractorToGlobal, displayName: invoiceOut.contractorToName } as IEntitySimpleDTO;

                        if (e.isBaseDocumentInfoChanged) {
                           let baseDocumentInfo = { idTable: idTable, idGlobal: idDocument, displayName: e.baseDocumentInfo.displayName };
                           let documentCostInfo = { supplierCostInfo: supplierCostInfo, retailCostInfo: retailCostInfo} as IDocumentItemCostInfoDTO;
                           invoiceCashOrder.baseDocumentSet.setValue(documentEdit, baseDocumentInfo, documentCostInfo, contractorTo);
                        }
                    }
                    });
                }
        }
    }

    const updateIncludeVat = (idContractorGlobal: string) => {
        contractorDataProvider.getById(idContractorGlobal, (contractor) => {
            if (contractor) {
                setIsIncludeVAT(contractor.useVat);
            }
        });
    }

    return(
        <>
            <BaseDocumentCreator
                valid={true}
                ok={{
                    onClick: onOk,
                    disabled: documentStateProc || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting
                }}
                save={{
                    onClick: onSave,
                    disabled: documentStateProc || props.isSubmitting,
                    sendRequestGlobal: props.isSubmitting
                }}
                print={{
                    onClick: () => {
                        return IdTableVariant.InvoiceCashOrder;
                    },
                    onSubItemClick: () => {
                        let arr = new Array<IKeyValuePair>();
                        if (props.document != undefined && props.document.mnemocode != undefined && props.idGlobal != undefined) arr.push({ key: props.idGlobal, value: props.document.mnemocode });
                        return arr;
                    },
                }}
                cancel={{ onClick: onCancel }}
            >
                <div className={сreateInvoiceCashOrderStyles.glwrapper}>
                    <div className={сreateInvoiceCashOrderStyles.wrapper}>
                        <div>
                            <FlexColumn>
                                <GridWrapper cols={35}>
                                    <FlexRow className={сreateInvoiceCashOrderStyles.compactFieldFields} wrap>
                                        <TextInput
                                            disabled
                                            label={baseT("number")}
                                            value={mnemocode}
                                            inline
                                        />
                                        <DateInput
                                            required
                                            disabled={documentStateProc}
                                            labelVariant="required"
                                            value={invoiceCashOrder.documentDate.value}
                                            onBlur={(value) => value && invoiceCashOrder.documentDate.setValue(value)}
                                            label={baseT("date")}
                                            error={errors.documentDate}
                                            inline
                                        />
                                    </FlexRow>
                                </GridWrapper>
                                <GridWrapper cols={2}>
                                    <ContractorSelectorInput
                                        disabled={documentStateProc}
                                        label={baseT("mainContractor")}
                                        selectedEntity={invoiceCashOrder.contractorOwner.value}
                                        onSelect={(value) => {
                                            invoiceCashOrder.contractorOwnerSet.setValue(value.idGlobal, value);
                                        }}
                                        onClear={() => {
                                            invoiceCashOrder.idContractorOwnerGlobal.setValue(uuidNull);
                                        }}
                                        treeViewCheckDirectoryType="pharmacy"
                                        inline
                                    />
                                    <TextInput
                                        label={baseT("baseText")}
                                        value={invoiceCashOrder.baseText.value}
                                        onChange={(value) => value && invoiceCashOrder.baseText.setValue(value)
                                        }
                                        disabled={documentStateProc}
                                        inline
                                    />
                                </GridWrapper>
                                <GridWrapper cols={2}>
                                    <ContractorSelectorInput
                                        required
                                        disabled={documentStateProc}
                                        label={baseT("from")}
                                        selectedEntity={invoiceCashOrder.contractorFrom.value}
                                        onSelect={(value) => {
                                            let isContractorFromNotChanged = value.idGlobal == invoiceCashOrder.idContractorFromGlobal;
                                            let retailSumIncVatNew = isContractorFromNotChanged ? invoiceCashOrder.retailSumIncVat.value : 0;
                                            let idTaxTypeGlobalNew = isContractorFromNotChanged ? invoiceCashOrder.idTaxTypeGlobal.value : uuidNull;
                                            let vatSumNew = isContractorFromNotChanged ? invoiceCashOrder.retailVatSum.value : 0;
                                            let baseDocumentInfoNew = isContractorFromNotChanged ? invoiceCashOrder.baseDocumentInfo.value : { idTable: 0, idDocument: uuidNull } as IDocumentInfoDTO;

                                            invoiceCashOrder.contractorFromSet.setValue(isContractorFromNotChanged, value.idGlobal, value, retailSumIncVatNew, idTaxTypeGlobalNew,
                                                vatSumNew, baseDocumentInfoNew);
                                        }}
                                        onClear=
                                        {() => {
                                            invoiceCashOrder.contractorFromClear.setValue(uuidNull, 0, uuidNull, null);
                                        }}
                                        error={errors.contractorFrom}
                                        treeViewCheckDirectoryType="none"
                                        gridFilterStrict={true}
                                        searchQuery={invoiceCashOrder.contractorFrom.value?.displayName}
                                        excludeUserFilter={true}
                                        applyUserFilter={false}
                                        inline
                                    />
                                    <TextInput
                                        label={baseT("supplement")}
                                        value={invoiceCashOrder.supplement.value}
                                        onChange={(value) => value && invoiceCashOrder.supplement.setValue(value)}
                                        disabled={documentStateProc}
                                        inline
                                    />
                                </GridWrapper>
                                <GridWrapper cols={2}>
                                    <GridWrapper cols={4}>
                                        <DecimalInputV2
                                            required
                                            disabled={documentStateProc}
                                            label={baseT("sumPay")}
                                            inputId={"paySum"}
                                            value={invoiceCashOrder.retailSumIncVat.value}
                                            onChange={(value) => {
                                                let vatSum = 0;
                                                if (invoiceCashOrder.taxRate.value === undefined || invoiceCashOrder.taxRate.value === null) {
                                                    vatSum = invoiceCashOrder.retailVatSum.value
                                                }
                                                else {
                                                    vatSum = (isIncludeVAT ?
                                                        getVATSumIncludeVAT(value, invoiceCashOrder.taxRate.value) :
                                                        getVATSumNotIncludeVAT(value, invoiceCashOrder.taxRate.value));
                                                }
                                                invoiceCashOrder.sumPaySet.setValue(value, vatSum);
                                            }}
                                            error={errors.sumPay}
                                        />
                                        <TaxTypeSelectorInput
                                            label={baseT("taxType")}
                                            selectedEntity={invoiceCashOrder.supplierTaxType.value}
                                            onSelect={(value) => {
                                                invoiceCashOrder.taxTypeSet.setValue(value, value.idGlobal)
                                            }}
                                            onClear={() => {
                                                let vatSumOrigin = getVatSumOrigin();
                                                invoiceCashOrder.taxTypeClear.setValue(vatSumOrigin, uuidNull);
                                            }}
                                            disabled={documentStateProc}
                                        />
                                        <CheckBox
                                            id={`includeVat-${uuidv4()}`}
                                            label={baseT("includeVAT")}
                                            defaultChecked={isIncludeVAT}
                                            onChanged={(value) => {
                                                setIsIncludeVAT(value);
                                                if (invoiceCashOrder.taxRate.value !== undefined && invoiceCashOrder.taxRate.value !== null) {
                                                    let vatSum = value ? getVATSumIncludeVAT(invoiceCashOrder.retailSumIncVat.value, invoiceCashOrder.taxRate.value) : getVATSumNotIncludeVAT(invoiceCashOrder.retailSumIncVat.value, invoiceCashOrder.taxRate.value);
                                                    invoiceCashOrder.retailVatSum.setValue(vatSum);
                                                }
                                            }
                                            }
                                            disabled={!invoiceCashOrder.isIncludeVATActive.value}
                                        />
                                        <DecimalInputV2
                                            disabled={documentStateProc}
                                            label={baseT("vat")}
                                            inputId={"vat"}
                                            value={invoiceCashOrder.retailVatSum.value}
                                            onChange={(value) => {
                                                invoiceCashOrder.vatSet.setValue(value, uuidNull);
                                            }}
                                        />
                                    </GridWrapper>
                                    <GridWrapper cols={1}>
                                        <DictionaryInput
                                            className={сreateInvoiceCashOrderStyles.field_DocumentBase}
                                            placeholder="-- Не выбрано --"
                                            value={invoiceCashOrder.baseDocument.value}
                                            label={baseT("baseDocument")}
                                            onMoreClick={() => {
                                                setSelectBaseDocument(true);
                                            }}
                                            onClearClick={() => {
                                                invoiceCashOrder.baseDocumentClear.setValue();
                                            }}
                                            disabled={documentStateProc}
                                            inline
                                        />
                                    </GridWrapper>
                                </GridWrapper>
                            </FlexColumn>
                        </div>
                    </div>
                </div>

                {showMessageModal.show && (
                    <MessageModalWindow
                        message={showMessageModal.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModal({ show: false, message: "" });
                                showMessageModal.btSaveOrOk === "ok" ? onOkAction(entity) : props.save(entity);
                            },
                        }}
                        cancel={{
                            onClick: () => {
                                setShowMessageModal({ show: false, message: "" });
                            },
                        }}
                        primary
                    />
                )}

                {showMessageModalValidDocument.show && (
                    <MessageModalWindow
                        message={showMessageModalValidDocument.message}
                        ok={{
                            onClick: () => {
                                setShowMessageModalValidDocument({ show: false, message: "" });
                            },
                        }}
                        cancel={{ onClick: () => setShowMessageModalValidDocument({ show: false, message: "" }) }}
                    />
                )}

                {selectBaseDocument && (
                <SelectDocumentModalWindow
                idStoreGlobal={uuidNull}
                filterExtra={ invoiceCashOrder.idContractorFromGlobal.value && invoiceCashOrder.idContractorFromGlobal.value != uuidNull ? { idContractorToGlobal: invoiceCashOrder.idContractorFromGlobal.value, isGetLotByDocumentActive : false } as IDocumentGridFilterExtra :
                { isGetLotByDocumentActive : false } as IDocumentGridFilterExtra}
                dateDocument={invoiceCashOrder.documentDate.value}
                    ok={(lots, document) => {
                        invoiceCashOrder.baseDocument.setValue(document.displayName);
                        if (lots && lots?.length > 0) {
                        const values: IReturnCreatedItem[] = lots.map((x) => {
                            const idGlobal: string = uuidv4();
                            return {
                                displayItem: {
                                    idGlobal: idGlobal,
                                    idDocumentGlobal: props.idGlobal,
                                    supplierVat: x.supplierCostInfo?.vat ?? 0,
                                    supplierPrice: x.supplierCostInfo?.price ?? 0,
                                    supplierVatPrice: x.supplierCostInfo?.vat ?? 0,
                                    supplierPriceIncVat: x.supplierCostInfo?.priceIncVat ?? 0,
                                    goodsCode: x.goodsCode,
                                    producer: x.producer?.displayName,
                                    supplierSum: x.supplierCostInfo?.sum ?? 0,
                                    supplierVatSum: x.supplierCostInfo?.vatSum ?? 0,
                                    supplierSumIncVat: x.supplierCostInfo?.sumIncVat ?? 0,
                                    supplierAdprice: x.supplierCostInfo.adprice,
                                    retailVat: x.retailCostInfo?.vat ?? 0,
                                    retailPrice: x.retailCostInfo?.price ?? 0,
                                    retailVatPrice: x.retailCostInfo?.vat ?? 0,
                                    retailPriceIncVat: x.retailCostInfo?.priceIncVat ?? 0,
                                    retailSum: x.retailCostInfo?.sum ?? 0,
                                    retailVatSum: x.retailCostInfo?.vat ?? 0,
                                    retailSumIncVat: x.retailCostInfo?.sumIncVat ?? 0,
                                    retailAdprice: x.retailCostInfo.adprice,                                        
                                    goodsName: x.goods?.displayName,
                                    scalingRatioName: x.scalingRatio.displayName,
                                    quantity: x.quantityRem,
                                    contractorPricePerUnit: 0,
                                    isKiz: x.isKiz,
                                    supplier: x.supplier,
                                    kizCount: x.isKiz === false ? undefined : 0,
                                    idLotGlobal: x.idGlobal,
                                },
                                createdItem: {
                                    idGlobal: idGlobal,
                                    idGoodsGlobal: x.goods.idGlobal,
                                    idScalingRatioGlobal: x.scalingRatio.idGlobal,
                                    idLotGlobal: x.idGlobal,
                                    idLotFromGlobal: x.idGlobal,
                                    idSupplierGlobal: x.idSupplierGlobal,
                                    contractorPricePerUnit: 0,
                                    producerPrice: x.producerPrice,
                                    price: 0,
                                    priceVat: x.retailCostInfo?.vat ?? 0,
                                    vatSup: 0,
                                    isKiz: x.isKiz,
                                    retailCostInfo: x.retailCostInfo,
                                    supplierCostInfo: x.supplierCostInfo,
                                    kizs: [],
                                    kizBoxes: [],
                                    quantity: x.quantityRem ?? 0,
                                },
                                };
                            });
                        }

                        
                        let isBaseDocumentInfoChanged = (invoiceCashOrder.baseDocumentInfo.value?.idDocument !== document.idGlobal ||
                        invoiceCashOrder.baseDocumentInfo.value?.idTable !== document.idTable) ? true : false;

                        let baseDocumentChanged = { isBaseDocumentInfoChanged: isBaseDocumentInfoChanged,
                            baseDocumentInfo: { idTable: document.idTable, idGlobal: document.idGlobal, displayName: document.displayName }
                        } as IBaseDocumentChanged;

                        onChangeInput(baseDocumentChanged, invoiceCashOrder.document.value);

                    }}
                    cancel={{ onClick: () => setSelectBaseDocument(false) }}
                    
                    content={[ { name: DocumentTypeRu.cashSession}, { name : DocumentTypeRu.movement}, { name: DocumentTypeRu.invoiceOut} ]}
                />
            )}
            </BaseDocumentCreator>
        </>
    );
};